/**
 * Rest timer preferences with localStorage persistence
 */

import type { MovementPattern } from '../types';

export interface RestTimerPreferences {
  // Default rest times in seconds
  compoundRestTime: number; // For compound movements (squat, hinge, push, pull)
  isolationRestTime: number; // For isolation/accessory movements (core, warmup)
  // Audio/vibration settings
  audioEnabled: boolean;
  vibrationEnabled: boolean;
  // Sound countdown settings
  countdownBeepEnabled: boolean; // Beep at 3, 2, 1
  completionSoundEnabled: boolean; // Sound when rest is done
}

const DEFAULT_PREFERENCES: RestTimerPreferences = {
  compoundRestTime: 120, // 2 minutes for compound
  isolationRestTime: 60, // 1 minute for isolation
  audioEnabled: true,
  vibrationEnabled: true,
  countdownBeepEnabled: true,
  completionSoundEnabled: true,
};

const STORAGE_KEY = 'gf_rest_timer_prefs';

/**
 * Compound movement patterns that benefit from longer rest
 */
const COMPOUND_PATTERNS: MovementPattern[] = [
  'squat',
  'hinge',
  'lunge',
  'push_horizontal',
  'push_vertical',
  'pull_horizontal',
  'pull_vertical',
  'carry',
];

/**
 * Isolation/accessory patterns that need shorter rest
 */
const ISOLATION_PATTERNS: MovementPattern[] = [
  'core',
  'conditioning',
  'warmup',
];

/**
 * Check if a movement pattern is compound (needs longer rest)
 */
export function isCompoundMovement(pattern?: MovementPattern): boolean {
  if (!pattern) return true; // Default to compound if unknown
  return COMPOUND_PATTERNS.includes(pattern);
}

/**
 * Get recommended rest time based on movement pattern
 */
export function getRecommendedRestTime(pattern?: MovementPattern, preferences?: RestTimerPreferences): number {
  const prefs = preferences || getRestTimerPreferences();
  
  if (!pattern) {
    return prefs.compoundRestTime; // Default to compound
  }
  
  if (COMPOUND_PATTERNS.includes(pattern)) {
    return prefs.compoundRestTime;
  }
  
  if (ISOLATION_PATTERNS.includes(pattern)) {
    return prefs.isolationRestTime;
  }
  
  return prefs.compoundRestTime; // Default
}

/**
 * Get a human-readable label for the movement type
 */
export function getMovementTypeLabel(pattern?: MovementPattern): string {
  if (!pattern) return 'Standard';
  
  if (COMPOUND_PATTERNS.includes(pattern)) {
    return 'Compound';
  }
  
  if (ISOLATION_PATTERNS.includes(pattern)) {
    return 'Isolation';
  }
  
  return 'Standard';
}

/**
 * Load rest timer preferences from localStorage
 */
export function getRestTimerPreferences(): RestTimerPreferences {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to handle any missing keys from older versions
      return { ...DEFAULT_PREFERENCES, ...parsed };
    }
  } catch (err) {
    console.warn('Failed to load rest timer preferences:', err);
  }
  return { ...DEFAULT_PREFERENCES };
}

/**
 * Save rest timer preferences to localStorage
 */
export function saveRestTimerPreferences(prefs: Partial<RestTimerPreferences>): RestTimerPreferences {
  const current = getRestTimerPreferences();
  const updated = { ...current, ...prefs };
  
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (err) {
    console.warn('Failed to save rest timer preferences:', err);
  }
  
  return updated;
}

/**
 * Reset rest timer preferences to defaults
 */
export function resetRestTimerPreferences(): RestTimerPreferences {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    console.warn('Failed to reset rest timer preferences:', err);
  }
  return { ...DEFAULT_PREFERENCES };
}

/**
 * Format seconds as MM:SS
 */
export function formatRestTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins === 0) {
    return `${secs}s`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
